import React, { useCallback, useRef, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';

/**
 * The one confirmation dialog. Replaces window.confirm (unstyled, unfocusable by our tests,
 * invisible in screenshots) and gives the one-click job starts ("Run group", "Re-run") a
 * moment to name what is about to change.
 *
 *   const { confirm, confirmDialog } = useConfirm();
 *   …
 *   if (!(await confirm({ title: 'Run group?', body: 'Masks 3 destinations.', confirmLabel: 'Run' }))) return;
 *   …
 *   return (<>{confirmDialog}…</>);
 *
 * `variant` 'danger' for destructive actions (Delete …), 'primary' for starts. Esc / Cancel
 * resolve false. Cancel takes focus when the dialog opens so Enter never confirms by accident.
 */
export function ConfirmDialog({ show, title, body, confirmLabel = 'Confirm', cancelLabel = 'Cancel', variant = 'primary', onConfirm, onCancel, busy = false }) {
  const cancelRef = useRef(null);
  return (
    <Modal
      show={show}
      onHide={onCancel}
      onEntered={() => cancelRef.current && cancelRef.current.focus()}
      aria-labelledby='confirm-dialog-title'
      data-testid='confirm-dialog'
    >
      <Modal.Header closeButton>
        <Modal.Title id='confirm-dialog-title' style={{ fontSize: 16 }}>{title}</Modal.Title>
      </Modal.Header>
      {body && <Modal.Body style={{ whiteSpace: 'pre-line' }}>{body}</Modal.Body>}
      <Modal.Footer>
        <Button ref={cancelRef} variant='outline-secondary' onClick={onCancel} data-testid='confirm-dialog-cancel'>
          {cancelLabel}
        </Button>
        <Button variant={variant} onClick={onConfirm} disabled={busy} data-testid='confirm-dialog-confirm'>
          {confirmLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export function useConfirm() {
  const [state, setState] = useState(null); // { title, body, confirmLabel, variant, resolve }
  const confirm = useCallback(
    (options) =>
      new Promise((resolve) => {
        setState({ ...options, resolve });
      }),
    []
  );
  const settle = (value) => {
    if (state && state.resolve) state.resolve(value);
    setState(null);
  };
  const confirmDialog = (
    <ConfirmDialog
      show={Boolean(state)}
      title={state ? state.title : ''}
      body={state ? state.body : null}
      confirmLabel={state && state.confirmLabel ? state.confirmLabel : 'Confirm'}
      cancelLabel={state && state.cancelLabel ? state.cancelLabel : 'Cancel'}
      variant={state && state.variant ? state.variant : 'primary'}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  );
  return { confirm, confirmDialog };
}

export default ConfirmDialog;
